import { css, html, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type {
    Commerce,
    CommerceShareChannel,
    CommerceShareResource,
    CommerceShareTargetType,
} from "@teqtank/commerce-sdk";
import "./teqtank-qr.js";

@customElement("teqtank-share-panel")
export class TeqtankSharePanel extends LitElement {
    @property({ attribute: false }) public accessor commerce: Commerce | undefined;
    @property() public accessor type: CommerceShareTargetType = "storefront";
    @property({ attribute: "target-id" }) public accessor targetId = "";
    @property() public accessor heading = "Share";
    @property() public accessor description =
        "Create an storefront-attributed link for this experience.";
    @property({ type: Boolean, attribute: "show-qr" }) public accessor showQr = true;
    @property({ type: Boolean, attribute: "auto-create" })
    public accessor autoCreate = false;
    @state() private accessor resource: CommerceShareResource | undefined;
    @state() private accessor status = "";
    @state() private accessor message = "";
    @state() private accessor copyState: "idle" | "copied" | "failed" = "idle";
    public static override styles = css`
        :host {
            container-type: inline-size;
            display: block;
            font-family: var(--teqtank-font-body, Inter, ui-sans-serif, system-ui, sans-serif);
        }
        .panel {
            display: grid;
            gap: 1rem;
            padding: 1.15rem;
            border: 1px solid var(--teqtank-color-border, #ddd7cc);
            border-radius: var(--teqtank-share-panel-radius, 1rem);
            background: var(--teqtank-share-panel-background, var(--teqtank-color-surface, #fff));
        }
        h3,
        p {
            margin: 0;
        }
        .muted,
        .url {
            color: var(--teqtank-color-text-muted, #68645d);
        }
        .layout {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto;
            gap: 1.25rem;
            align-items: start;
        }
        .channels {
            display: flex;
            flex-wrap: wrap;
            gap: 0.55rem;
            margin-top: 0.85rem;
        }
        button,
        a {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0.65rem 0.9rem;
            border: 1px solid var(--teqtank-color-border, #ccc);
            border-radius: 999px;
            background: transparent;
            color: inherit;
            font: inherit;
            font-weight: 700;
            text-decoration: none;
            cursor: pointer;
        }
        .primary {
            background: var(--teqtank-color-accent, #01676b);
            border-color: var(--teqtank-color-accent, #01676b);
            color: #fff;
        }
        .url {
            overflow-wrap: anywhere;
            font-size: 0.85rem;
        }
        textarea,
        input {
            width: 100%;
            box-sizing: border-box;
            margin-top: 0.75rem;
            padding: 0.8rem;
            border: 1px solid var(--teqtank-color-border, #ccc);
            border-radius: 0.7rem;
            background: #fff;
            color: inherit;
            font: inherit;
        }
        textarea {
            min-height: 92px;
            resize: vertical;
        }
        .link {
            display: grid;
            grid-template-columns: 1fr auto;
            gap: 0.5rem;
            align-items: end;
        }
        .status {
            min-height: 1.2rem;
            color: var(--teqtank-color-accent, #01676b);
            font-size: 0.82rem;
        }
        .copy-feedback {
            grid-column: 1 / -1;
            min-height: 1.2rem;
            color: var(--teqtank-color-accent, #01676b);
            font-size: 0.82rem;
            font-weight: 700;
        }
        .copy-feedback[data-state="failed"] {
            color: var(--teqtank-color-danger, #9f2525);
        }
        @container (max-width: 580px) {
            .layout {
                grid-template-columns: 1fr;
            }
            .qr {
                display: grid;
                place-items: center;
            }
        }
    `;
    protected override firstUpdated(): void {
        if (this.autoCreate) void this.create();
    }
    protected override render() {
        return html`<section class="panel" part="panel">
            <h3 part="title">${this.heading}</h3>
            ${
                this.resource
                    ? this.renderResource()
                    : html`<p class="muted" part="description">${this.description}</p>
                          <button
                              class="primary"
                              part="primary-action create-action"
                              type="button"
                              @click=${this.create}
                          >
                              Create share link
                          </button>`
            }<span class="status" part="status" aria-live="polite">${this.status}</span>
        </section>`;
    }
    private renderResource() {
        const providers = this.commerce?.share.providers ?? [];
        return html`<div class="layout" part="layout">
            <div>
                <p part="resource-description">${this.resource!.description}</p>
                <label part="message-field"
                    >Your message<textarea
                        part="message-input"
                        .value=${this.message}
                        @input=${(event: InputEvent) => (this.message = (event.currentTarget as HTMLTextAreaElement).value)}
                    ></textarea>
                </label>
                <div class="link" part="link">
                    <input
                        part="link-input"
                        aria-label="Share link"
                        readonly
                        .value=${this.resource!.shortUrl}
                    /><button part="secondary-action copy-action" type="button" @click=${this.copy}>
                        ${this.copyState === "copied" ? "Copied ✓" : "Copy link"}
                    </button>
                    <span
                        class="copy-feedback"
                        data-state=${this.copyState}
                        part="copy-status"
                        role="status"
                        aria-live="polite"
                        >${
                            this.copyState === "copied"
                                ? "Share link copied to your clipboard."
                                : this.copyState === "failed"
                                  ? "Copy was blocked. Select the link and copy it manually."
                                  : ""
                        }</span
                    >
                </div>
                <div class="channels" part="channels">
                    <button
                        class="primary"
                        part="primary-action native-share-action"
                        type="button"
                        @click=${this.nativeShare}
                    >
                        Share</button
                    >${providers.map((provider) => {
                        const url = this.destinationUrl(provider.channel);
                        return url
                            ? html`<a
                                  href=${url}
                                  target="_blank"
                                  rel="noopener"
                                  data-channel=${provider.channel}
                                  part="channel channel-${provider.channel}"
                                  >${provider.label}</a
                              >`
                            : null;
                    })}
                </div>
            </div>
            ${
                this.showQr
                    ? html`<div class="qr" part="qr">
                          <teqtank-qr
                              part="qr-code"
                              exportparts="frame,image,logo,caption"
                              .src=${this.resource!.qrCodeUrl}
                              .value=${this.resource!.url}
                              caption="Scan to open"
                              downloadable
                          ></teqtank-qr>
                      </div>`
                    : null
            }
        </div>`;
    }
    private async create(): Promise<void> {
        if (!this.commerce) return;
        this.status = "Creating link…";
        try {
            this.resource = await this.commerce.share.create({
                type: this.type,
                ...(this.targetId ? { id: this.targetId } : {}),
            });
            this.message = this.resource.description;
            this.copyState = "idle";
            this.status = "Share link ready";
            this.dispatchEvent(
                new CustomEvent("teqtank-share-created", {
                    detail: this.resource,
                    bubbles: true,
                    composed: true,
                }),
            );
        } catch (error) {
            this.status =
                error instanceof Error ? error.message : "The share link could not be created.";
        }
    }
    private async nativeShare(): Promise<void> {
        if (!this.resource || !this.commerce) return;
        const shared =
            typeof navigator !== "undefined" && navigator.share
                ? await navigator
                      .share({
                          title: this.resource.title,
                          text: this.message,
                          url: this.resource.url,
                      })
                      .then(() => true)
                      .catch(() => false)
                : false;
        if (!shared) await this.copy();
    }
    private async copy(): Promise<void> {
        if (!this.resource || !this.commerce) return;
        const copied = await this.commerce.share.copy(this.resource);
        this.copyState = copied ? "copied" : "failed";
        this.status = copied ? "" : "Copy is not available in this browser";
    }

    private destinationUrl(channel: CommerceShareChannel): string | null {
        if (!this.resource) return null;
        const payload = encodeURIComponent(`${this.message} ${this.resource.url}`);
        const url = encodeURIComponent(this.resource.url);
        switch (channel) {
            case "email":
                return `mailto:?subject=${encodeURIComponent(this.resource.title)}&body=${payload}`;
            case "sms":
                return `sms:?body=${payload}`;
            case "whatsapp":
                return `https://wa.me/?text=${payload}`;
            case "facebook":
                return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
            default:
                return this.commerce?.share.destinationUrl(this.resource, channel) ?? null;
        }
    }
}
declare global {
    interface HTMLElementTagNameMap {
        "teqtank-share-panel": TeqtankSharePanel;
    }
}
